// ==================== VERSION ====================
export const APP_VERSION = '0.3.1';
export const APP_BUILD_DATE = '2026-02-26';

// ==================== TRIAL CONFIGURATION ====================
export const TRIAL_DAYS = 14;
export const TRIAL_KEY = 'arrangementlab_trial_start';
export const TRIAL_EMAIL_KEY = 'arrangementlab_user_email';
export const TRIAL_NAME_KEY = 'arrangementlab_user_name';

export function getTrialInfo() {
  const start = localStorage.getItem(TRIAL_KEY);
  if (!start) {
    // Trial hasn't started yet — don't auto-start, wait for email capture
    return { daysLeft: TRIAL_DAYS, expired: false, started: null, hasStarted: false };
  }
  const elapsed = Date.now() - parseInt(start);
  const daysElapsed = Math.floor(elapsed / (1000 * 60 * 60 * 24));
  const daysLeft = Math.max(0, TRIAL_DAYS - daysElapsed);
  return { daysLeft, expired: daysLeft === 0, started: parseInt(start), hasStarted: true };
}

export function startTrial() {
  if (!localStorage.getItem(TRIAL_KEY)) {
    localStorage.setItem(TRIAL_KEY, Date.now().toString());
  }
}

export function getTrialEmail() {
  return localStorage.getItem(TRIAL_EMAIL_KEY) || null;
}

export function saveTrialUser(email, name = '') {
  localStorage.setItem(TRIAL_EMAIL_KEY, email);
  if (name) localStorage.setItem(TRIAL_NAME_KEY, name);
}

// ==================== EARTH TONE COLORS ====================
export const EARTH = {
  stone: { 50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917' },
  sage: { 50: '#f6f7f4', 100: '#e8ebe3', 200: '#d4dac9', 300: '#b5c0a5', 400: '#94a67e', 500: '#758c5f', 600: '#5c7049', 700: '#49593b', 800: '#3c4832', 900: '#343d2d' },
  olive: { 50: '#f7f7f2', 100: '#ecece2', 200: '#d9d9c6', 300: '#c2c1a3', 400: '#a9a77d', 500: '#918e5e', 600: '#76724a', 700: '#5d5a3a', 800: '#4d4a35', 900: '#423f30' },
  sand: { 50: '#fdfcfa', 100: '#f9f6f1', 200: '#f2ebe0', 300: '#e6dacb', 400: '#d4c4ad', 500: '#bfa888', 600: '#a68d6d', 700: '#8a735a', 800: '#72604d', 900: '#5e5042' },
  terracotta: { 50: '#fdf6f3', 100: '#fceae3', 200: '#f9d5c7', 300: '#f4b8a1', 400: '#ec9172', 500: '#c9593a', 600: '#cf5535', 700: '#a84830', 800: '#8f3a28', 900: '#773426' },
  forest: { 50: '#f3f6f4', 100: '#e1e9e3', 200: '#c5d4c9', 300: '#9db7a5', 400: '#72957d', 500: '#527860', 600: '#3f604c', 700: '#344d3e', 800: '#2c3f34', 900: '#26352c' },
  clay: { 50: '#fbf7f5', 100: '#f6ede8', 200: '#eddbd1', 300: '#e0c2b1', 400: '#cfa389', 500: '#c08968', 600: '#b27454', 700: '#945e46', 800: '#7a4f3d', 900: '#664435' },
  // Updated to match landing page
  cream: '#DDD9D3',
  paper: '#ebe6d7',
  ink: '#1a1a1a',
  sepia: '#555',
  gold: '#c9593a',
};

// Typography - Barlow Condensed for headers, Outfit for body
export const TYPOGRAPHY = {
  display: "'Barlow Condensed', sans-serif",
  header: "'Barlow Condensed', sans-serif",
  body: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
};

// CSS injection for Barlow Condensed + Outfit fonts and distinctive styling
if (typeof document !== 'undefined' && !document.getElementById('arrangementlab-fonts')) {
  const link = document.createElement('link');
  link.id = 'arrangementlab-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap';
  document.head.appendChild(link);

  // Refined design system — Ive precision x underground roots
  const style = document.createElement('style');
  style.id = 'arrangementlab-artsy-styles';
  style.textContent = `
    /* ========== SHADOW SYSTEM ========== */
    /* Refined offset shadow — warm undertone, lighter than before */
    .neo-shadow {
      box-shadow: 2px 2px 0px rgba(41, 37, 36, 0.06), 0 1px 3px rgba(0,0,0,0.04);
      transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .neo-shadow:hover {
      box-shadow: 3px 3px 0px rgba(41, 37, 36, 0.08), 0 2px 8px rgba(0,0,0,0.06);
      transform: translate(-0.5px, -0.5px);
    }
    .neo-shadow:active {
      box-shadow: 1px 1px 0px rgba(41, 37, 36, 0.06);
      transform: translate(0.5px, 0.5px);
    }

    /* Elevated shadow — for modals and floating elements */
    .shadow-elevated {
      box-shadow: 0 4px 24px rgba(41, 37, 36, 0.08), 0 1px 4px rgba(0,0,0,0.04);
    }

    /* ========== GLASS & SURFACE ========== */
    .glass-panel {
      background: rgba(253, 252, 250, 0.92);
      backdrop-filter: blur(12px) saturate(1.2);
      -webkit-backdrop-filter: blur(12px) saturate(1.2);
      border: 1px solid rgba(214, 211, 209, 0.5);
    }

    /* ========== TYPOGRAPHY ========== */
    .anton-header {
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }

    /* ========== TEXTURE & ORGANIC ========== */
    .texture-overlay {
      position: relative;
    }
    .texture-overlay::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.025;
      pointer-events: none;
      mix-blend-mode: multiply;
      border-radius: inherit;
    }

    .organic-radius {
      border-radius: 14px 18px 16px 20px;
    }

    .hand-underline {
      position: relative;
    }
    .hand-underline::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      right: 0;
      height: 2px;
      background: currentColor;
      opacity: 0.2;
      border-radius: 1px;
      transform: skewX(-3deg);
    }

    /* ========== INTERACTIONS ========== */
    .playful-hover {
      transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .playful-hover:hover {
      transform: scale(1.015) rotate(0.3deg);
    }

    /* Subtle entrance animation */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .fade-up {
      animation: fadeUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    }

    /* ========== BADGES & PILLS ========== */
    .artsy-badge {
      background: linear-gradient(135deg, #c9593a, #a84830);
      padding: 3px 10px;
      border-radius: 20px;
      font-size: 10px;
      font-weight: 600;
      color: white;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      box-shadow: 1px 1px 0px rgba(0,0,0,0.1);
    }

    /* ========== INPUTS ========== */
    .artsy-input {
      border: 1.5px solid #e7e5e4;
      border-radius: 12px;
      transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      background: #fdfcfa;
    }
    .artsy-input:focus {
      border-color: #c9593a;
      box-shadow: 0 0 0 3px rgba(201, 89, 58, 0.08);
      outline: none;
      background: #fff;
    }

    /* ========== STRUCTURAL ========== */
    .accent-stripe {
      position: relative;
      overflow: hidden;
    }
    .accent-stripe::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 3px;
      height: 100%;
      background: currentColor;
      border-radius: 0 2px 2px 0;
    }

    /* Section divider */
    .divider-line {
      height: 1px;
      background: linear-gradient(90deg, transparent, #d6d3d1, transparent);
    }

    /* ========== SCROLLBAR ========== */
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #a8a29e; }

    /* ========== SELECTION ========== */
    ::selection { background: rgba(201, 89, 58, 0.15); color: inherit; }
  `;
  document.head.appendChild(style);
}

export const CARD_COLORS = [
  { id: 'sage', name: 'Sage', bg: EARTH.sage[50], border: EARTH.sage[300], text: EARTH.sage[800], accent: EARTH.sage[500] },
  { id: 'sand', name: 'Sand', bg: EARTH.sand[50], border: EARTH.sand[300], text: EARTH.sand[800], accent: EARTH.sand[600] },
  { id: 'olive', name: 'Olive', bg: EARTH.olive[50], border: EARTH.olive[300], text: EARTH.olive[800], accent: EARTH.olive[500] },
  { id: 'terracotta', name: 'Terracotta', bg: EARTH.terracotta[50], border: EARTH.terracotta[300], text: EARTH.terracotta[800], accent: EARTH.terracotta[500] },
  { id: 'forest', name: 'Forest', bg: EARTH.forest[50], border: EARTH.forest[300], text: EARTH.forest[800], accent: EARTH.forest[500] },
  { id: 'clay', name: 'Clay', bg: EARTH.clay[50], border: EARTH.clay[300], text: EARTH.clay[800], accent: EARTH.clay[500] },
  { id: 'stone', name: 'Stone', bg: EARTH.stone[50], border: EARTH.stone[300], text: EARTH.stone[700], accent: EARTH.stone[500] },
];

export const SECTION_COLORS = { intro: CARD_COLORS[0], verse: CARD_COLORS[1], chorus: CARD_COLORS[3], bridge: CARD_COLORS[2], solo: CARD_COLORS[5], outro: CARD_COLORS[4], hook: CARD_COLORS[3], drop: CARD_COLORS[4], breakdown: CARD_COLORS[6], build: CARD_COLORS[2], default: CARD_COLORS[6] };
export const SECTION_TYPES = [
  { id: 'intro', name: 'Intro', defaultBars: 4 },
  { id: 'verse', name: 'Verse', defaultBars: 8 },
  { id: 'chorus', name: 'Chorus', defaultBars: 8 },
  { id: 'bridge', name: 'Bridge', defaultBars: 8 },
  { id: 'hook', name: 'Hook', defaultBars: 4 },
  { id: 'solo', name: 'Solo', defaultBars: 16 },
  { id: 'drop', name: 'Drop', defaultBars: 8 },
  { id: 'breakdown', name: 'Breakdown', defaultBars: 8 },
  { id: 'build', name: 'Build', defaultBars: 8 },
  { id: 'outro', name: 'Outro', defaultBars: 4 },
];
export const STATUS_COLORS = { 'not-started': { bg: EARTH.stone[100], border: EARTH.stone[300], text: EARTH.stone[600] }, 'in-progress': { bg: EARTH.sand[100], border: EARTH.sand[400], text: EARTH.sand[700] }, 'done': { bg: EARTH.sage[100], border: EARTH.sage[400], text: EARTH.sage[700] } };
export const CANVAS_THEMES = { light: { bg: EARTH.stone[50], grid: EARTH.stone[200] }, warm: { bg: EARTH.sand[50], grid: EARTH.sand[200] }, sage: { bg: EARTH.sage[50], grid: EARTH.sage[200] }, cream: { bg: EARTH.cream, grid: EARTH.sand[300] }, dark: { bg: EARTH.stone[900], grid: EARTH.stone[700] } };
export const STICKY_COLORS = [{ id: 'sand', color: EARTH.sand[200], border: EARTH.sand[400] }, { id: 'sage', color: EARTH.sage[200], border: EARTH.sage[400] }, { id: 'olive', color: EARTH.olive[200], border: EARTH.olive[400] }, { id: 'clay', color: EARTH.clay[200], border: EARTH.clay[400] }, { id: 'stone', color: EARTH.stone[200], border: EARTH.stone[400] }, { id: 'forest', color: EARTH.forest[200], border: EARTH.forest[400] }, { id: 'terracotta', color: EARTH.terracotta[200], border: EARTH.terracotta[400] }];
export const CONNECTOR_COLORS = [{ id: 'stone', color: EARTH.stone[500] }, { id: 'sage', color: EARTH.sage[500] }, { id: 'olive', color: EARTH.olive[500] }, { id: 'terracotta', color: EARTH.terracotta[500] }, { id: 'forest', color: EARTH.forest[500] }, { id: 'clay', color: EARTH.clay[500] }];
export const KEY_SIGNATURES = ['C Major', 'G Major', 'D Major', 'A Major', 'E Major', 'B Major', 'F Major', 'Bb Major', 'Eb Major', 'Ab Major', 'A Minor', 'E Minor', 'D Minor', 'G Minor', 'C Minor', 'F Minor'];

// SPRINT 4: Zoom sensitivity reduced (was 0.9/1.1, now 0.97/1.03)
export const ZOOM_FACTOR = 0.03; // 3% per scroll tick

export const getSectionType = (name) => { const l = name.toLowerCase(); if (l.includes('intro')) return 'intro'; if (l.includes('verse')) return 'verse'; if (l.includes('chorus')) return 'chorus'; if (l.includes('hook')) return 'hook'; if (l.includes('bridge')) return 'bridge'; if (l.includes('solo')) return 'solo'; if (l.includes('outro')) return 'outro'; if (l.includes('drop')) return 'drop'; if (l.includes('breakdown')) return 'breakdown'; if (l.includes('build')) return 'build'; return 'default'; };

// Duration calculation helpers
export const calculateDuration = (bars, tempo, beatsPerBar = 4) => {
  if (!tempo || tempo <= 0) return '0:00';
  const totalBeats = bars * beatsPerBar;
  const totalSeconds = (totalBeats * 60) / tempo;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const calculateDurationSeconds = (bars, tempo, beatsPerBar = 4) => {
  if (!tempo || tempo <= 0) return 0;
  return (bars * beatsPerBar * 60) / tempo;
};

// SPRINT 4: Calculate bars needed for target duration
export const calculateBarsForDuration = (targetMinutes, tempo, beatsPerBar = 4) => {
  const targetSeconds = targetMinutes * 60;
  return Math.round((targetSeconds * tempo) / (beatsPerBar * 60));
};
